import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { AiOutlineDown } from "react-icons/ai";
import logo from "@/assets/nav_logo.png";
import Wlogo from "@/assets/bottom-logo.png";
import "./index.less";
import { RightCircleOutlined } from '@ant-design/icons';

const NewNav = () => {
  const location = useLocation();
  const pathname = location?.pathname;
  const [activeDropdown, setActiveDropdown] = useState(-1);
  const [isVisible, setIsVisible] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const lastScrollY = useRef(0);

  // 检查是否在首页
  const isHomePage = pathname === "/";

  // 判断是否应该显示WLogo：首页且未滚动
  const shouldShowWLogo = isHomePage && isAtTop;

  // 滚动监听效果
  useEffect(() => {
    if (!isHomePage) {
      // 如果不是首页，直接设置为非顶部状态
      setIsAtTop(false);
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // 检测是否在页面顶部
      setIsAtTop(scrollTop < 10);
      
      // 检测滚动方向，实现导航栏的显示/隐藏
      if (scrollTop > lastScrollY.current && scrollTop > 90) {
        // 向下滚动超过100px，隐藏导航栏
        setIsVisible(false);
      } else if (scrollTop < lastScrollY.current || scrollTop < 50) {
        // 向上滚动或接近顶部，显示导航栏
        setIsVisible(true);
      }
      
      lastScrollY.current = scrollTop;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {window.removeEventListener('scroll', handleScroll)};
  }, [isHomePage]);

  // 使用useMemo包装数据，避免每次渲染重新创建
  const navItems = useMemo(() => [
    { path: "/", key: "/", name: "首页" },
    { path: "/AIServer", key: "AIServer", name: "AI服务器"},
    // { path: "/Nous", key: "Nous", name: "Nous HP61" },
    { path: "/solution", key: "solution", name: "解决方案" },
    { path: "/news", key: "news", name: "新闻中心" },   
    { path: "/services", key: "services", name: "公司业务" },
    { path: "/about", key: "About", name: "关于信维" },
  ], []);

  // 修复active状态判断逻辑
  const isActiveLink = useCallback((navItem) => {
    if (pathname === "/") {
      return navItem.key === "/";
    }
    
    // 对于有下拉菜单的项，检查是否是其子路径
    if (navItem.hasDropdown) {
      return pathname.startsWith(navItem.path);
    }
    
    // 对于普通项，精确匹配路径
    return pathname === navItem.path;
  }, [pathname]);

  const handleProductsClick = useCallback((e, index) => {
    e.preventDefault();
    // 点击产品时切换下拉菜单显示
    const newActiveState = activeDropdown === index ? -1 : index;
    setActiveDropdown(newActiveState);
  }, [activeDropdown]);

  const handleSubItemClick = useCallback(() => {
    // 点击子项时立即关闭下拉菜单
    setActiveDropdown(-1);
  }, []);

  // 点击页面其他区域关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeDropdown !== -1 && 
          !e.target.closest(".nav_item_content") &&
          !e.target.closest(".dropdown-menu-container")) {
        setActiveDropdown(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  // 监听模态框打开/关闭状态，隐藏导航栏
  useEffect(() => {
    let isModalOpen = false;
    
    const checkModalOpen = () => {
      // 检查是否有打开的 Modal（antd Modal 会在 body 上添加类名或属性）
      const modalWraps = document.querySelectorAll('.ant-modal-wrap');
      const hasOpenModal = Array.from(modalWraps).some(modal => {
        const style = window.getComputedStyle(modal);
        return style.display !== 'none' && modal.getAttribute('aria-hidden') !== 'true';
      });
      
      // 如果状态发生变化，更新导航栏可见性
      if (hasOpenModal !== isModalOpen) {
        isModalOpen = hasOpenModal;
        
        if (hasOpenModal) {
          setIsVisible(false);
        } else {
          // 如果没有打开的模态框，恢复原来的可见性逻辑
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          if (isHomePage) {
            setIsAtTop(scrollTop < 10);
            if (scrollTop > lastScrollY.current && scrollTop > 90) {
              setIsVisible(false);
            } else if (scrollTop < lastScrollY.current || scrollTop < 50) {
              setIsVisible(true);
            }
          } else {
            setIsVisible(true);
          }
        }
      }
    };

    // 使用 MutationObserver 监听 DOM 变化
    const observer = new MutationObserver(checkModalOpen);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
      childList: true,
      subtree: true
    });

    // 初始检查
    checkModalOpen();

    // 定期检查（作为备用方案，确保不会遗漏）
    const intervalId = setInterval(checkModalOpen, 200);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, [isHomePage]);

  return (
    <div 
      className={`new_nav_page ${!isAtTop ? 'scrolled' : ''} ${isHomePage ? 'home-page' : 'other-page'} ${isVisible ? 'visible' : 'hidden'}`}
    >
      <div className="nav_warp">
        <div className="logo">
          <Link to="/">
            {/* 根据条件显示不同的Logo */}
            {shouldShowWLogo ? (
              <img src={Wlogo} alt="White Logo" className="logo-image" />
            ) : (
              <img src={logo} alt="Logo" className="logo-image" />
            )}
          </Link>
        </div>

        <div className="nav_list">
          {navItems.map((item, index) => {
            const isActive = isActiveLink(item);
            return (
              <div
                className="nav_item"
                key={item.key}
              >
                <div className="nav_item_content">
                  <Link
                    to={item.path}
                    className={isActive ? "active" : ""}
                    onClick={item.hasDropdown ? (e) => handleProductsClick(e, index) : undefined}
                  >
                    {item.name}
                    {item.hasDropdown && (
                      <AiOutlineDown 
                        className={`icon ${activeDropdown === index ? 'rotated' : ''}`} 
                      />
                    )}
                  </Link>
                  
                  {/* 添加底部指示条 */}
                  {!item.hasDropdown && (
                    <div className={`nav_indicator ${isActive ? 'active' : ''}`}></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 独立的下拉菜单，基于new_nav_page定位 */}
      {activeDropdown !== -1 && navItems[activeDropdown].hasDropdown && (
        <div
          className="dropdown-menu-container"
        >
          <div className="dropdown-menu">
            <div className="cover">
              <div className="word-in">
                 <h5>Products</h5>
                 <p>A rich variety of products and diverse choices.</p>
              </div>
            </div>
            <div className="jump-item">
              {navItems[activeDropdown].dropdownData.map((product) => (
                <Link
                  key={product.id}
                  to={`/AIproduct/${product.id}`}
                  className="dropdown-item"
                  onClick={handleSubItemClick}
                >
                  <span className="product-text">{product.series}</span>
                  <RightCircleOutlined className="arrow-icon" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewNav;